/**
 * Canonical Network Store - Singleton
 *
 * Store centralizado para gestionar el estado visual de la red canónica (nodos y edges).
 *
 * Responsabilidades:
 * - Almacenar nodos y edges por ID
 * - Proporcionar interfaz para consultas rápidas (getNode, getEdge, getConnectedEdges)
 * - Generar FeatureCollection GeoJSON para actualizar MapLibre source
 * - Gestionar merge incremental de features
 * - Sincronizar con source de MapLibre
 *
 * NO responsable de:
 * - Lógica de negocio (eso va en servicios)
 * - Llamadas HTTP (eso va en API services)
 * - Lógica de preview temporal (eso irá en un store separado en Fase 4)
 * - Gestión de feature-state de MapLibre (eso se maneja externamente)
 */

import type { Feature, Point, LineString, FeatureCollection, Geometry } from 'geojson';
import type { Map as MapLibreMap, GeoJSONSource } from 'maplibre-gl';
import { performanceProfiler } from '../services/performance-profiler.service';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Propiedades de un nodo canónico (GeoJSON Feature properties)
 */
export interface CanonicalNodeProperties {
  id: string;
  type: 'node';
  intersectionType?: string | null;
  nodeVersion?: number;
  // Otras propiedades según necesidad
  [key: string]: unknown;
}

/**
 * Propiedades de un edge canónico (GeoJSON Feature properties)
 */
export interface CanonicalEdgeProperties {
  id: string;
  type: 'edge';
  roadClass?: string | null;
  fromNodeId: string;
  toNodeId: string;
  lengthMeters?: number | null;
  edgeVersion?: number;
  // Otras propiedades según necesidad
  [key: string]: unknown;
}

/**
 * Feature de nodo canónico (GeoJSON Feature con Point geometry)
 */
export type CanonicalNodeFeature = Feature<Point, CanonicalNodeProperties>;

/**
 * Feature de edge canónico (GeoJSON Feature con LineString geometry)
 */
export type CanonicalEdgeFeature = Feature<LineString, CanonicalEdgeProperties>;

/**
 * Cualquier feature canónico (nodo o edge)
 */
export type CanonicalFeature = CanonicalNodeFeature | CanonicalEdgeFeature;

/**
 * FeatureCollection de red canónica
 */
export type CanonicalNetworkFeatureCollection = FeatureCollection<
  Geometry,
  CanonicalNodeProperties | CanonicalEdgeProperties
>;

// ============================================================================
// STORE CLASS
// ============================================================================

/**
 * Store singleton para red canónica
 */
class CanonicalNetworkStore {
  // Almacenamiento interno por ID
  private nodes: Map<string, CanonicalNodeFeature> = new Map();
  private edges: Map<string, CanonicalEdgeFeature> = new Map();

  // Source ID de MapLibre
  private readonly SOURCE_ID = 'canonical-network';

  // Referencia al mapa (opcional, para auto-sync)
  private map: MapLibreMap | null = null;

  /**
   * Inicializa el store con features desde backend
   * Reemplaza todo el contenido anterior
   *
   * @param features Array de features (nodos y edges)
   */
  initialize(features: CanonicalFeature[]): void {
    // BUG 3 INSTRUMENTACIÓN: Log detallado de inicialización
    console.log('[DIAG:STORE] ═══════════════════════════════════════');
    console.log('[DIAG:STORE] initialize() called');
    console.log('[DIAG:STORE] Input features count:', features.length);

    // Detectar caller para diagnóstico
    const callerStack = new Error().stack?.split('\n').slice(2, 4).join(' <- ') || 'unknown';
    console.log('[DIAG:STORE] Called from:', callerStack);

    this.clear();

    for (const feature of features) {
      if (!feature.properties?.id) {
        console.warn('[CanonicalNetworkStore] Feature sin ID, ignorando:', feature);
        continue;
      }

      if (feature.properties.type === 'node') {
        this.nodes.set(feature.properties.id, feature as CanonicalNodeFeature);
      } else if (feature.properties.type === 'edge') {
        this.edges.set(feature.properties.id, feature as CanonicalEdgeFeature);
      } else {
        console.warn('[CanonicalNetworkStore] Feature con type desconocido:', feature.properties.type);
      }
    }

    console.log('[DIAG:STORE] After initialize:', {
      nodes: this.nodes.size,
      edges: this.edges.size,
      total: this.nodes.size + this.edges.size,
    });
    console.log('[DIAG:STORE] ═══════════════════════════════════════');

    console.log(
      `[CanonicalNetworkStore] Initialized with ${this.nodes.size} nodes, ${this.edges.size} edges`
    );
  }

  /**
   * Limpia todo el store
   */
  clear(): void {
    // BUG 3 INSTRUMENTACIÓN: Log detallado de clear
    const beforeNodes = this.nodes.size;
    const beforeEdges = this.edges.size;

    console.log('[DIAG:STORE] ⚠️ clear() called');
    console.log('[DIAG:STORE] Before clear:', { nodes: beforeNodes, edges: beforeEdges });

    // Detectar caller para diagnóstico
    const callerStack = new Error().stack?.split('\n').slice(2, 5).join('\n    ') || 'unknown';
    console.log('[DIAG:STORE] Called from:\n    ', callerStack);

    this.nodes.clear();
    this.edges.clear();
    console.log('[CanonicalNetworkStore] Store cleared');
  }

  /**
   * Merge incremental de features
   * - Si el feature existe (mismo ID), lo reemplaza
   * - Si no existe, lo agrega
   *
   * @param features Features a mergear
   */
  merge(features: CanonicalFeature[]): void {
    // PERF: Instrumentación para profiling
    const session = performanceProfiler.getCurrentSession();
    const sessionId = session?.sessionId || 'no-session';
    performanceProfiler.markStageStart(sessionId, 'store-merge', {
      featureCount: features.length,
    });

    // BUG 3 INSTRUMENTACIÓN: Log detallado de merge
    const beforeNodes = this.nodes.size;
    const beforeEdges = this.edges.size;

    console.log('[DIAG:STORE] ───────────────────────────────────────');
    console.log('[DIAG:STORE] merge() called');
    console.log('[DIAG:STORE] Input features count:', features.length);
    console.log('[DIAG:STORE] Before merge:', { nodes: beforeNodes, edges: beforeEdges });

    // Detectar caller para diagnóstico
    const callerStack = new Error().stack?.split('\n').slice(2, 4).join(' <- ') || 'unknown';
    console.log('[DIAG:STORE] Called from:', callerStack);

    let nodesAdded = 0;
    let edgesAdded = 0;

    for (const feature of features) {
      if (!feature.properties?.id) {
        console.warn('[CanonicalNetworkStore] Feature sin ID en merge, ignorando:', feature);
        continue;
      }

      if (feature.properties.type === 'node') {
        this.nodes.set(feature.properties.id, feature as CanonicalNodeFeature);
        nodesAdded++;
      } else if (feature.properties.type === 'edge') {
        this.edges.set(feature.properties.id, feature as CanonicalEdgeFeature);
        edgesAdded++;
      }
    }

    console.log('[DIAG:STORE] After merge:', {
      nodes: this.nodes.size,
      edges: this.edges.size,
      nodesAdded,
      edgesAdded
    });
    console.log('[DIAG:STORE] ───────────────────────────────────────');

    console.log(
      `[CanonicalNetworkStore] Merged ${nodesAdded} nodes, ${edgesAdded} edges. ` +
      `Total: ${this.nodes.size} nodes, ${this.edges.size} edges`
    );

    performanceProfiler.markStageEnd(sessionId, 'store-merge', {
      nodesAdded,
      edgesAdded,
      totalNodes: this.nodes.size,
      totalEdges: this.edges.size,
    });
  }

  /**
   * Elimina features por ID
   *
   * @param ids Array de IDs a eliminar
   */
  remove(ids: string[]): void {
    // BUG 3 INSTRUMENTACIÓN: Log detallado de remove
    const beforeNodes = this.nodes.size;
    const beforeEdges = this.edges.size;

    console.log('[DIAG:STORE] ───────────────────────────────────────');
    console.log('[DIAG:STORE] remove() called');
    console.log('[DIAG:STORE] IDs to remove:', ids);
    console.log('[DIAG:STORE] Before remove:', { nodes: beforeNodes, edges: beforeEdges });

    let removed = 0;

    for (const id of ids) {
      if (this.nodes.delete(id)) {
        removed++;
      } else if (this.edges.delete(id)) {
        removed++;
      }
    }

    console.log('[DIAG:STORE] After remove:', {
      nodes: this.nodes.size,
      edges: this.edges.size,
      removed
    });
    console.log('[DIAG:STORE] ───────────────────────────────────────');

    console.log(
      `[CanonicalNetworkStore] Removed ${removed} features. ` +
      `Total: ${this.nodes.size} nodes, ${this.edges.size} edges`
    );
  }

  /**
   * Actualiza features en el store
   * Alias de merge() para claridad semántica
   *
   * @param features Features a actualizar
   */
  updateFeatures(features: CanonicalFeature[]): void {
    this.merge(features);
  }

  /**
   * Actualiza features en el store Y sincroniza con el source de MapLibre
   *
   * @param features Features a actualizar
   */
  updateAndSync(features: CanonicalFeature[]): void {
    this.merge(features);
    this.syncToSource();
  }

  /**
   * Genera FeatureCollection GeoJSON con todos los features actuales
   * Este es el formato que MapLibre source.setData() espera
   *
   * @returns FeatureCollection con todos los nodos y edges
   */
  toFeatureCollection(): CanonicalNetworkFeatureCollection {
    const features: CanonicalFeature[] = [
      ...Array.from(this.edges.values()), // Edges primero (se renderizan debajo)
      ...Array.from(this.nodes.values()), // Nodes después (se renderizan encima)
    ];

    return {
      type: 'FeatureCollection',
      features,
    };
  }

  /**
   * Obtiene un nodo por ID
   *
   * @param nodeId ID del nodo
   * @returns Feature del nodo o undefined si no existe
   */
  getNode(nodeId: string): CanonicalNodeFeature | undefined {
    return this.nodes.get(nodeId);
  }

  /**
   * Obtiene un edge por ID
   *
   * @param edgeId ID del edge
   * @returns Feature del edge o undefined si no existe
   */
  getEdge(edgeId: string): CanonicalEdgeFeature | undefined {
    return this.edges.get(edgeId);
  }

  /**
   * Obtiene todos los edges conectados a un nodo
   *
   * @param nodeId ID del nodo
   * @returns Array de features de edges conectados
   */
  getConnectedEdges(nodeId: string): CanonicalEdgeFeature[] {
    const connectedEdges: CanonicalEdgeFeature[] = [];

    for (const edge of this.edges.values()) {
      if (edge.properties.fromNodeId === nodeId || edge.properties.toNodeId === nodeId) {
        connectedEdges.push(edge);
      }
    }

    return connectedEdges;
  }

  /**
   * Obtiene todos los IDs de nodos
   *
   * @returns Array de IDs de nodos
   */
  getNodeIds(): string[] {
    return Array.from(this.nodes.keys());
  }

  /**
   * Obtiene todos los IDs de edges
   *
   * @returns Array de IDs de edges
   */
  getEdgeIds(): string[] {
    return Array.from(this.edges.keys());
  }

  /**
   * Obtiene estadísticas del store
   *
   * @returns Objeto con contadores
   */
  getStats(): { nodes: number; edges: number } {
    return {
      nodes: this.nodes.size,
      edges: this.edges.size,
    };
  }

  /**
   * Vincula el store a un mapa de MapLibre
   * Permite que el store sincronice automáticamente con el source
   *
   * @param map Instancia de MapLibre Map
   */
  setMap(map: MapLibreMap): void {
    this.map = map;
    console.log('[CanonicalNetworkStore] Map reference set');
  }

  /**
   * Sincroniza el store con el source de MapLibre
   * Llama a source.setData() con el FeatureCollection actual
   *
   * IMPORTANTE: Esto reemplaza TODA la data del source
   * MapLibre internamente hace diff y solo re-renderiza lo que cambió
   */
  syncToSource(): void {
    // PERF: Instrumentación para profiling
    const session = performanceProfiler.getCurrentSession();
    const sessionId = session?.sessionId || 'no-session';
    performanceProfiler.markStageStart(sessionId, 'store-syncToSource');

    if (!this.map) {
      console.warn('[CanonicalNetworkStore] Cannot sync: map reference not set');
      performanceProfiler.markStageEnd(sessionId, 'store-syncToSource', { error: 'no-map' });
      return;
    }

    const source = this.map.getSource(this.SOURCE_ID) as GeoJSONSource;
    if (!source) {
      console.warn(`[CanonicalNetworkStore] Source "${this.SOURCE_ID}" not found in map`);
      performanceProfiler.markStageEnd(sessionId, 'store-syncToSource', { error: 'no-source' });
      return;
    }

    const featureCollection = this.toFeatureCollection();

    // Solo alertar en casos anómalos (errores reales, no diagnóstico)
    if (featureCollection.features.length === 0) {
      console.error('[CanonicalNetworkStore] ⚠️ syncToSource con 0 features');
    }

    performanceProfiler.markStageStart(sessionId, 'maplibre-setData', {
      featureCount: featureCollection.features.length,
    });

    source.setData(featureCollection as any);

    performanceProfiler.markStageEnd(sessionId, 'maplibre-setData', {
      featureCount: featureCollection.features.length,
    });

    performanceProfiler.markStageEnd(sessionId, 'store-syncToSource', {
      featureCount: featureCollection.features.length,
    });
  }

  /**
   * Verifica si el store está vacío
   *
   * @returns true si no hay nodos ni edges
   */
  isEmpty(): boolean {
    return this.nodes.size === 0 && this.edges.size === 0;
  }

  /**
   * Obtiene todos los nodos como array
   *
   * @returns Array de features de nodos
   */
  getAllNodes(): CanonicalNodeFeature[] {
    return Array.from(this.nodes.values());
  }

  /**
   * Obtiene todos los edges como array
   *
   * @returns Array de features de edges
   */
  getAllEdges(): CanonicalEdgeFeature[] {
    return Array.from(this.edges.values());
  }
}

// ============================================================================
// SINGLETON EXPORT
// ============================================================================

/**
 * Instancia singleton del store canónico
 *
 * USO:
 *
 * import { canonicalNetworkStore } from './canonical-network-store';
 *
 * // Inicializar con datos del backend
 * canonicalNetworkStore.initialize(features);
 *
 * // Vincular al mapa
 * canonicalNetworkStore.setMap(map);
 *
 * // Sincronizar con source
 * canonicalNetworkStore.syncToSource();
 *
 * // Consultar features
 * const node = canonicalNetworkStore.getNode('node-id');
 * const edges = canonicalNetworkStore.getConnectedEdges('node-id');
 *
 * // Actualizar y sincronizar
 * canonicalNetworkStore.updateAndSync([updatedNode, ...updatedEdges]);
 */
export const canonicalNetworkStore = new CanonicalNetworkStore();

// Exponer store a window para debugging y E2E tests
if (typeof window !== 'undefined') {
  (window as any).canonicalNetworkStore = canonicalNetworkStore;
}

// ============================================================================
// UTILITIES (opcional, para facilitar conversiones)
// ============================================================================

/**
 * Convierte datos raw del backend a CanonicalFeature
 *
 * @param rawData Datos del backend (node o edge)
 * @returns CanonicalFeature
 */
export function rawToFeature(rawData: any): CanonicalFeature | null {
  if (!rawData.id || !rawData.geometry) {
    console.warn('[rawToFeature] Datos inválidos:', rawData);
    return null;
  }

  // Detectar tipo por geometría
  const type = rawData.geometry.type === 'Point' ? 'node' : 'edge';

  if (type === 'node') {
    return {
      type: 'Feature',
      geometry: rawData.geometry,
      properties: {
        id: rawData.id,
        type: 'node',
        intersectionType: rawData.intersectionType,
        nodeVersion: rawData.nodeVersion,
      },
    } as CanonicalNodeFeature;
  } else {
    return {
      type: 'Feature',
      geometry: rawData.geometry,
      properties: {
        id: rawData.id,
        type: 'edge',
        roadClass: rawData.roadClass,
        fromNodeId: rawData.fromNode?.id || rawData.fromNodeId,
        toNodeId: rawData.toNode?.id || rawData.toNodeId,
        lengthMeters: rawData.lengthMeters,
        edgeVersion: rawData.edgeVersion,
      },
    } as CanonicalEdgeFeature;
  }
}

/**
 * Convierte array de datos raw a array de CanonicalFeature
 *
 * @param rawDataArray Array de datos del backend
 * @returns Array de CanonicalFeature
 */
export function rawArrayToFeatures(rawDataArray: any[]): CanonicalFeature[] {
  return rawDataArray
    .map(rawToFeature)
    .filter((f): f is CanonicalFeature => f !== null);
}
